// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — écarts et état d'une campagne (DÉMONSTRATION) :
//   • écarts (livraison partielle, prix final différent, perte, vol, invendu, retour d'une unité remise, autre) :
//     déclarés, puis décidés par une AUTRE personne avec un traitement fermé (mouvement de stock en ajout seul ; une
//     créance ne se corrige que par un avoir de la trésorerie) ;
//   • ÉTAT : « N unités achetées : à qui attribuées et où est passé chaque montant ? » — unités (équation physique),
//     fournisseurs (facturé, payé, reste), salariés (brut, aide, constitué, restant dû, retenues bloquées), contrôles,
//     export CSV tracé ; créances de la campagne et leurs actes de gestion (proposés, puis validés par une autre
//     personne : suspension, reprise, report, rééchelonnement).
// Routes : /api/dispositifs/campagnes/:id/{ecarts,etat,etat.csv}, /ecarts/:id/decision ; FINANCE /creances/:id/actes,
// /actes/:id/decider (Idempotency-Key).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Gavel, MoreVertical, Plus, Scale, Wrench } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, MenuActions } from '../../design/composants';
import { toArray, libelle } from './api';
import { Champ, Etat } from './commun';
import { francs, dateLongue, lireMontant, ETATS_CREANCE } from './apiSocial';
import {
  useLectureSociale, EtatsLecture, FenetreActe, TitreSection, Tuiles, Montant, motifValide, ChampMotif, ChoixRadio, usePieceDepot, ChampPiece,
  BandeauComptaDemo, BandeauRetenuesSimulation,
} from './communSocial';
import { BadgeSimulation } from './EcheanciersCommun';
import {
  useCampagnes, quantiteUnites, STATUTS_ECART, TYPES_ECART, TRAITEMENTS, TRAITEMENTS_PAR_TYPE, ECARTS_AVEC_QUANTITE,
  ETATS_INSCRIPTION, STATUTS_COMMANDE,
} from './apiCampagnes';
import { BoutonExportEtat, LienCentre, porteeCampagne, lireEntier } from './communCampagnes';
import { FenetreProposerActe, FenetreDeciderActe } from './ActesCreance';

// ─── Écarts ───────────────────────────────────────────────────────────────────────────────────
const AIDES_ECART = {
  LIVRAISON_PARTIELLE: 'Quantité non livrée par rapport à la commande. L\'attribution se calcule sur le reçu net ; un avoir du fournisseur peut être à établir.',
  PRIX_DIFFERENT: 'Avant les remises, un prix final différent se règle par une nouvelle version datée du règlement au Centre de configuration : les inscriptions déjà faites gardent leur prix figé. Après les remises, déclarez l\'écart : la correction des créances passe par un avoir de la trésorerie, proposé puis validé par deux personnes.',
  PERTE: 'Unités perdues (mortalité, casse) : à la décision, elles sortent du stock.',
  VOL: 'Unités volées : à la décision, elles sortent du stock.',
  INVENDU: 'Unités restées en stock : constat, revente ou retour au fournisseur.',
  RETOUR: 'Une unité remise revient : désignez la remise ; l\'unité revient en stock sur son lot. La créance du salarié ne se corrige que par un avoir de la trésorerie.',
  AUTRE: 'Tout autre écart, décrit précisément.',
};

function FenetreEcart({ campagne, onFermer, onFait }) {
  const api = useCampagnes();
  const achats = useLectureSociale(() => api.achats(campagne.id), [api, campagne.id]);
  const remises = useLectureSociale(() => api.remises(campagne.id), [api, campagne.id]);
  const stock = useLectureSociale(() => api.stock(campagne.id), [api, campagne.id]);
  const [type, setType] = useState('PERTE');
  const [quantite, setQuantite] = useState('');
  const [montant, setMontant] = useState('');
  const [commande, setCommande] = useState('');
  const [remise, setRemise] = useState('');
  const [lot, setLot] = useState('');
  const [description, setDescription] = useState('');
  const [avecPiece, setAvecPiece] = useState(false);
  const piece = usePieceDepot(campagne.organisation_id, 'CAMPAGNE', campagne.id, 'JUSTIFICATIF');
  const q = quantite.trim() ? lireEntier(quantite) : null;
  const m = montant.trim() ? lireMontant(montant) : null;
  const quantiteRequise = ECARTS_AVEC_QUANTITE.includes(type);
  const pret = motifValide(description, 5) && (!quantiteRequise || (q && q > 0)) && (type !== 'RETOUR' || remise)
    && (!quantite.trim() || q !== null) && (!montant.trim() || m !== null) && (!avecPiece || piece.prete);
  const commandes = toArray(achats.donnees?.commandes).filter((k) => k.numero);
  const lots = toArray(stock.donnees?.lots);
  return (
    <FenetreActe titre="Déclarer un écart" Icone={AlertTriangle} libelleBouton="Déclarer" pret={!!pret} large onFermer={onFermer}
      aide="L'écart est décidé par une autre personne. Un désistement après attribution (avant toute remise) s'enregistre depuis la liste des inscriptions."
      onConfirmer={async () => {
        const pc = avecPiece ? await piece.deposer() : null;
        await api.declarerEcart(campagne.id, {
          type, description: description.trim(), ...(q !== null ? { quantite: q } : {}), ...(m !== null ? { montant: m } : {}),
          ...(commande ? { commande_id: commande } : {}), ...(remise ? { remise_id: remise } : {}), ...(lot ? { lot_id: lot } : {}), ...(pc ? { piece_id: pc.id } : {}),
        });
        onFait('Écart déclaré : il attend la décision d\'une autre personne.');
      }}>
      <Champ libelle="Type d'écart" requis>
        <select className="rh-champ-saisie" value={type} onChange={(e) => setType(e.target.value)}>
          {Object.entries(TYPES_ECART).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Champ>
      <p className="rh-form-aide" style={{ margin: '-4px 0 12px' }}>{AIDES_ECART[type]}{type === 'PRIX_DIFFERENT' && <> <LienCentre portee={porteeCampagne(campagne)} /></>}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle={`Quantité (${campagne.unite_pluriel})`} requis={quantiteRequise}><input className="rh-champ-saisie" inputMode="numeric" value={quantite} onChange={(e) => setQuantite(e.target.value)} /></Champ>
        <Champ libelle="Montant (F)"><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
      </div>
      {type === 'RETOUR' ? (
        <Champ libelle="Remise concernée" requis>
          <select className="rh-champ-saisie" value={remise} onChange={(e) => setRemise(e.target.value)}>
            <option value="">Choisir…</option>
            {toArray(remises.donnees).filter((r) => r.statut !== 'ANNULEE').map((r) => <option key={r.id} value={r.id}>{r.numero} — {r.salarie} ({quantiteUnites(campagne, r.quantite)})</option>)}
          </select>
        </Champ>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <Champ libelle="Commande concernée">
            <select className="rh-champ-saisie" value={commande} onChange={(e) => setCommande(e.target.value)}>
              <option value="">Aucune</option>
              {commandes.map((k) => <option key={k.id} value={k.id}>{k.numero} — {k.raison_sociale}</option>)}
            </select>
          </Champ>
          <Champ libelle="Lot concerné" aide="À défaut, les lots les plus anciens en stock.">
            <select className="rh-champ-saisie" value={lot} onChange={(e) => setLot(e.target.value)}>
              <option value="">Aucun en particulier</option>
              {lots.map((l) => <option key={l.lot_id} value={l.lot_id}>{l.libelle} ({l.en_stock} en stock)</option>)}
            </select>
          </Champ>
        </div>
      )}
      <ChampMotif valeur={description} onChange={setDescription} libelleChamp="Description" aide="5 caractères au moins : ce qui s'est passé, quand, constaté par qui." />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 12 }}>
        <input type="checkbox" checked={avecPiece} onChange={(e) => setAvecPiece(e.target.checked)} />Joindre une pièce (constat, bon de retour…)
      </label>
      {avecPiece && <ChampPiece piece={piece} types={['JUSTIFICATIF', 'PV', 'AVOIR', 'AUTRE']} />}
    </FenetreActe>
  );
}

function FenetreDeciderEcart({ ecart, campagne, onFermer, onFait }) {
  const api = useCampagnes();
  const traitements = TRAITEMENTS_PAR_TYPE[ecart.type] || [];
  const [decision, setDecision] = useState('VALIDER');
  const [traitement, setTraitement] = useState(traitements[0] || '');
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Décider : ${libelle(TYPES_ECART, ecart.type).toLowerCase()}`} Icone={Gavel} libelleBouton={decision === 'VALIDER' ? 'Valider' : 'Rejeter'} danger={decision !== 'VALIDER'}
      pret={motifValide(motif, 3) && (decision !== 'VALIDER' || traitement)} onFermer={onFermer}
      aide="La personne qui a déclaré l'écart ne le décide pas (double regard). Le traitement produit seulement des mouvements de stock en ajout seul."
      onConfirmer={async (cle) => { await api.deciderEcart(ecart.id, { decision, motif: motif.trim(), ...(decision === 'VALIDER' ? { traitement } : {}) }, cle); onFait('Écart décidé.'); }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>
        {ecart.quantite ? `${quantiteUnites(campagne, ecart.quantite)} · ` : ''}{ecart.montant ? `${francs(ecart.montant)} · ` : ''}« {ecart.description} »
      </p>
      <ChoixRadio nom="decision-ecart" options={{ VALIDER: 'Valider l\'écart', REJETER: 'Rejeter l\'écart' }} valeur={decision} onChange={setDecision} />
      {decision === 'VALIDER' && (
        <Champ libelle="Traitement" requis>
          <select className="rh-champ-saisie" value={traitement} onChange={(e) => setTraitement(e.target.value)}>
            {traitements.map((t) => <option key={t} value={t}>{TRAITEMENTS[t]}</option>)}
          </select>
        </Champ>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins. Le motif est conservé dans l'historique." />
    </FenetreActe>
  );
}

export function EcartsVue({ campagne, droits }) {
  const api = useCampagnes();
  const l = useLectureSociale(() => api.ecarts(campagne.id), [api, campagne.id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const ecarts = toArray(l.donnees);
  const fait = (m) => { setFenetre(null); setInfo(m); l.recharger(); };
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'flex-end' }}>
        {droits.a('ds_ecart_declarer') && <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'declarer' })}>Déclarer un écart</Bouton>}
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={l} Icone={Scale} estVide={!ecarts.length} titreVide="Aucun écart"
          vide="Livraison partielle, prix final différent, pertes, invendus, retours : chaque écart est déclaré puis décidé par une autre personne, sans jamais réécrire une remise ni une créance.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Écart</th><th>Quantité</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Description</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {ecarts.map((e) => (
                  <tr key={e.id}>
                    <td>{libelle(TYPES_ECART, e.type)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>déclaré le {dateLongue(e.declare_le)}</span></td>
                    <td>{e.quantite ? quantiteUnites(campagne, e.quantite) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{e.montant ? <Montant n={e.montant} /> : '—'}</td>
                    <td><Etat table={STATUTS_ECART} code={e.statut} />{e.traitement && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{libelle(TRAITEMENTS, e.traitement)}</span>}</td>
                    <td style={{ maxWidth: 320 }}>{e.description}</td>
                    <td style={{ textAlign: 'right' }}>{e.statut === 'DECLARE' && droits.a('ds_ecart_decider') && <Bouton petit Icone={Gavel} onClick={() => setFenetre({ type: 'decider', e })}>Décider</Bouton>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EtatsLecture>
      </section>
      {fenetre?.type === 'declarer' && <FenetreEcart campagne={campagne} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'decider' && <FenetreDeciderEcart ecart={fenetre.e} campagne={campagne} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}

// ─── État de campagne ─────────────────────────────────────────────────────────────────────────
const Controle = ({ ok, libelleOk, libelleKo }) => (ok ? <Badge ton="succes" point>{libelleOk}</Badge> : <Badge ton="danger" point>{libelleKo}</Badge>);
const td = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function Physique({ campagne, p }) {
  const u = (n) => quantiteUnites(campagne, n);
  return (
    <section className="rh-carte">
      <TitreSection>Unités : d'où elles viennent, où elles sont</TitreSection>
      <Tuiles tuiles={[
        { libelle: 'Commandées', valeur: u(p.commande) }, { libelle: 'Reçues', valeur: u(p.recu), aide: p.refuse ? `${p.refuse} refusée(s) à la réception` : undefined },
        { libelle: 'Remises', valeur: u(p.remis) }, { libelle: 'En stock', valeur: u(p.en_stock) },
        { libelle: 'Pertes', valeur: u(p.perdu) }, { libelle: 'Retours au fournisseur', valeur: u(p.retourne_fournisseur) },
        { libelle: 'Revendues', valeur: u(p.revendu) }, { libelle: 'Retours de salariés', valeur: u(p.retour_salarie) },
      ]} />
      <p style={{ margin: 0, fontSize: 14 }}>
        Reçu + retours de salariés = <strong>{p.equation?.entrees}</strong> ; remis + en stock + pertes + retours fournisseur + reventes = <strong>{p.equation?.sorties}</strong> ;
        écart : <strong>{p.equation?.ecart}</strong>.
      </p>
    </section>
  );
}

function Fournisseurs({ campagne, lignes }) {
  const somme = (k) => lignes.reduce((s, x) => s + Number(x[k] || 0), 0);
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      <div style={{ padding: '12px 16px 0' }}><TitreSection>Fournisseurs : ce qui a été commandé, reçu, facturé et payé</TitreSection></div>
      {!lignes.length ? <p className="rh-cellule-secondaire" style={{ margin: 0, padding: '0 16px 16px' }}>Aucune commande validée.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Commande</th><th>Quantité</th><th style={td}>Commandé</th><th>Reçu</th><th style={td}>Net facturé</th><th style={td}>Payé</th><th style={td}>En cours</th><th style={td}>Reste à payer</th></tr></thead>
            <tbody>
              {lignes.map((k) => (
                <tr key={k.commande_id}>
                  <td>{k.numero}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{k.fournisseur} · {libelle(STATUTS_COMMANDE, k.statut)}</span></td>
                  <td>{quantiteUnites(campagne, k.quantite)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>à {francs(k.prix_unitaire)}</span></td>
                  <td style={td}>{francs(k.montant)}</td>
                  <td>{k.recu}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{francs(k.valeur_recue)}</span></td>
                  <td style={td}>{francs(k.net_facture)}{k.avoirs ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>dont avoirs {francs(k.avoirs)}</span> : null}</td>
                  <td style={td}>{francs(k.paye)}</td>
                  <td style={td}>{francs(k.paiements_en_cours)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{francs(k.reste_a_payer)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td>Total</td><td>{quantiteUnites(campagne, somme('quantite'))}</td><td style={td}>{francs(somme('montant'))}</td><td>{somme('recu')}</td>
                <td style={td}>{francs(somme('net_facture'))}</td><td style={td}>{francs(somme('paye'))}</td><td style={td}>{francs(somme('paiements_en_cours'))}</td><td style={td}>{francs(somme('reste_a_payer'))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function Salaries({ campagne, lignes, totaux, droits, onActe }) {
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      <div style={{ padding: '12px 16px 0' }}><TitreSection>Salariés : à qui les unités, et où est passé chaque montant</TitreSection></div>
      {!lignes.length ? <p className="rh-cellule-secondaire" style={{ margin: 0, padding: '0 16px 16px' }}>Aucune inscription.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead>
              <tr>
                <th>Salarié</th><th>Demandé / attribué / remis</th><th style={td}>Brut</th><th style={td}>Aide</th><th style={td}>Constitué</th><th style={td}>Restant dû</th><th>Retenues</th><th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {lignes.map((x) => {
                const items = x.creance ? [
                  droits.a('ds_creance_mouvement_proposer') && ['ACTIVE', 'DIRECTE', 'SUSPENDUE'].includes(x.creance.etat) && { libelle: 'Proposer un acte de gestion', Icone: Wrench, onClick: () => onActe({ type: 'proposer', x }) },
                  droits.a('ds_creance_mouvement_valider') && { libelle: 'Décider d\'un acte proposé', Icone: CheckCircle2, onClick: () => onActe({ type: 'decider', x }) },
                ].filter(Boolean) : [];
                return (
                  <tr key={x.inscription_id}>
                    <td>
                      <span className="rh-cellule-principale">{x.salarie}</span>
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{x.numero} · {x.matricule} · {libelle(ETATS_INSCRIPTION, x.statut)}</span>
                      {x.remises && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Remises : {x.remises}</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{x.demande} / {x.attribue ?? '—'} / {x.remis || 0}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{campagne.unite_pluriel}</span></td>
                    <td style={td}>{francs(x.montant_brut)}</td>
                    <td style={td}>{francs(x.aide_imputee)}</td>
                    <td style={td}>{francs(x.montant_constitue)}</td>
                    <td style={td}>
                      {x.creance ? (
                        <>
                          <Link className="rh-lien" to={`/dispositifs/${campagne.organisation_id}/tresorerie/creances/${x.creance.id}`}>{francs(x.creance.solde)}</Link>
                          <span style={{ display: 'block' }}><Etat table={ETATS_CREANCE} code={x.creance.etat} /> <BadgeSimulation actif={x.creance.simulation} /></span>
                        </>
                      ) : <span className="rh-cellule-secondaire">Aucune créance</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {x.paie.proposees ? <>{x.paie.proposees} proposée(s)<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{x.paie.bloquees} bloquée(s) · retenu {francs(x.paie.retenu)}</span></> : <span className="rh-cellule-secondaire">—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{items.length > 0 && <MenuActions iconeSeule gauche Icone={MoreVertical} libelle={`Actes sur la créance de ${x.salarie}`} items={items} />}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td>Total</td><td style={{ whiteSpace: 'nowrap' }}>— / {totaux.unites_attribuees} / {totaux.unites_remises}</td>
                <td style={td}>{francs(totaux.montant_brut)}</td><td style={td}>{francs(totaux.aide_totale)}</td><td style={td}>{francs(totaux.montant_constitue)}</td>
                <td style={td}>{francs(totaux.soldes)}</td><td style={{ whiteSpace: 'nowrap' }}>retenu {francs(totaux.retenu_paie)}</td><td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

export function EtatVue({ campagne, droits }) {
  const api = useCampagnes();
  // Agrégé par défaut (décision du 13/10) ; le détail nominatif est demandé seulement avec ds_export_nominatif (serveur juge).
  const nominatif = droits.a('ds_export_nominatif');
  const l = useLectureSociale(() => api.etat(campagne.id, { nominatif }), [api, campagne.id, nominatif]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const e = l.donnees;
  const salaries = toArray(e?.salaries);
  const bloquees = e?.retenues ? Number(e.retenues.bloquees || 0) : salaries.reduce((s, x) => s + Number(x.paie?.bloquees || 0), 0);
  const proposees = e?.retenues ? Number(e.retenues.proposees || 0) : salaries.reduce((s, x) => s + Number(x.paie?.proposees || 0), 0);
  const fait = (m) => { setFenetre(null); setInfo(m); l.recharger(); };
  return (
    <>
      <BandeauComptaDemo />
      <BandeauRetenuesSimulation />
      <div className="rh-outils" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <p className="rh-form-aide" style={{ margin: 0, maxWidth: 640 }}>Tout est recalculé à partir des actes enregistrés (commandes, réceptions, mouvements de stock, remises, créances, paiements, retenues) ; rien n'est saisi.</p>
        {nominatif && <BoutonExportEtat campagne={campagne} libelleBouton="Exporter l'état nominatif (CSV)" />}
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <EtatsLecture lecture={l} Icone={Scale} estVide={false}>
        {e && (
          <>
            <section className="rh-carte">
              <TitreSection>Contrôles</TitreSection>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Controle ok={e.controles?.equation_physique_juste} libelleOk="Unités : équation juste" libelleKo="Unités : écart à expliquer" />
                <Controle ok={e.controles?.equation_salaries_juste} libelleOk="Montants salariés : équation juste" libelleKo="Montants salariés : écart à expliquer" />
                <Controle ok={!e.controles?.remises_sans_mouvement} libelleOk="Chaque remise a sa sortie de stock" libelleKo={`${e.controles?.remises_sans_mouvement} remise(s) sans sortie de stock`} />
              </div>
              <Tuiles tuiles={[
                { libelle: 'Unités attribuées', valeur: quantiteUnites(campagne, e.totaux?.unites_attribuees) },
                { libelle: 'Unités remises', valeur: quantiteUnites(campagne, e.totaux?.unites_remises) },
                { libelle: 'Montant brut des remises', valeur: francs(e.totaux?.montant_brut) },
                { libelle: 'Aide accordée', valeur: francs(e.totaux?.aide_totale) },
                { libelle: 'Constitué en créances', valeur: francs(e.totaux?.creances_constituees), aide: `brut − aide = ${francs(e.equation_salaries?.brut_moins_aide)}` },
                { libelle: 'Restant dû', valeur: francs(e.totaux?.soldes) },
              ]} />
            </section>
            <Physique campagne={campagne} p={e.physique || {}} />
            <Fournisseurs campagne={campagne} lignes={toArray(e.fournisseurs)} />
            {e.nominatif ? <Salaries campagne={campagne} lignes={salaries} totaux={e.totaux || {}} droits={droits} onActe={setFenetre} /> : (
              <section className="rh-carte">
                <TitreSection>Salariés (vue agrégée)</TitreSection>
                <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Le détail nominatif (« à qui, et où est passé chaque montant ») est réservé aux personnes habilitées ; chaque consultation est tracée.</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                  {Object.entries(e.inscriptions_par_statut || {}).map(([k, x]) => <li key={k}>{libelle(ETATS_INSCRIPTION, k)} : {x.inscriptions} inscription(s), {quantiteUnites(campagne, x.remis)} remise(s)</li>)}
                </ul>
              </section>
            )}
            <section className="rh-carte">
              <TitreSection>Retenues proposées à la Paie</TitreSection>
              <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                {proposees ? `${proposees} retenue(s) proposée(s), dont ${bloquees} bloquée(s).` : 'Aucune retenue proposée pour l\'instant.'}{' '}
                À chaque remise, les échéances de la créance sont proposées à la Paie ; elles restent <strong>bloquées</strong> tant que le fondement juridique de la retenue
                (cession ou accord validé) n'est pas enregistré et que la transmission à la Paie n'est pas ouverte. Une créance de campagne de simulation n'est jamais prélevée.
              </p>
              <Link className="rh-lien" to={`/dispositifs/${campagne.organisation_id}/retenues`}>Voir le détail et les motifs de blocage (onglet Retenues)</Link>
            </section>
            {toArray(e.ecarts).length > 0 && (
              <section className="rh-carte">
                <TitreSection>Écarts</TitreSection>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                  {toArray(e.ecarts).map((x) => <li key={x.id}>{libelle(TYPES_ECART, x.type)}{x.quantite ? ` — ${quantiteUnites(campagne, x.quantite)}` : ''} · {libelle(STATUTS_ECART, x.statut)}{x.traitement ? ` (${libelle(TRAITEMENTS, x.traitement).toLowerCase()})` : ''}</li>)}
                </ul>
              </section>
            )}
          </>
        )}
      </EtatsLecture>
      {fenetre?.type === 'proposer' && <FenetreProposerActe ligne={fenetre.x} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'decider' && <FenetreDeciderActe ligne={fenetre.x} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}
